// PhoneInput — telefon s výběrem mezinárodní předvolby + tvrdým formátem čísla.
// - číslo jde zadat JEN jako číslice, automaticky se formátuje s mezerami: "777 777 777"
// - předvolba: select s předvolbami celého světa, default CZ (+420), SK (+421) hned druhá
// - emituje plnou hodnotu "+420 777 777 777" (kompatibilní s backend normalizací)
//
// Použití: <PhoneInput value={telefon} onChange={setTelefon} disabled={saving} />

import React, { useMemo } from "react";

// Emoji vlajka z ISO kódu země (CZ → 🇨🇿).
const flagOf = (iso) => iso.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

// CZ + SK první, zbytek světa abecedně. V selectu se ukazuje jen vlajka + předvolba.
const DIAL_CODES = [
  { c: "+420", i: "cz" }, { c: "+421", i: "sk" },
  { c: "+93", i: "af" }, { c: "+355", i: "al" }, { c: "+213", i: "dz" }, { c: "+376", i: "ad" },
  { c: "+244", i: "ao" }, { c: "+54", i: "ar" }, { c: "+374", i: "am" }, { c: "+61", i: "au" },
  { c: "+994", i: "az" }, { c: "+973", i: "bh" }, { c: "+880", i: "bd" }, { c: "+32", i: "be" },
  { c: "+501", i: "bz" }, { c: "+375", i: "by" }, { c: "+229", i: "bj" }, { c: "+591", i: "bo" },
  { c: "+387", i: "ba" }, { c: "+267", i: "bw" }, { c: "+55", i: "br" }, { c: "+359", i: "bg" },
  { c: "+226", i: "bf" }, { c: "+86", i: "cn" }, { c: "+45", i: "dk" }, { c: "+243", i: "cd" },
  { c: "+20", i: "eg" }, { c: "+593", i: "ec" }, { c: "+372", i: "ee" }, { c: "+251", i: "et" },
  { c: "+63", i: "ph" }, { c: "+358", i: "fi" }, { c: "+33", i: "fr" }, { c: "+995", i: "ge" },
  { c: "+233", i: "gh" }, { c: "+30", i: "gr" }, { c: "+502", i: "gt" }, { c: "+852", i: "hk" },
  { c: "+56", i: "cl" }, { c: "+385", i: "hr" }, { c: "+91", i: "in" }, { c: "+62", i: "id" },
  { c: "+964", i: "iq" }, { c: "+98", i: "ir" }, { c: "+353", i: "ie" }, { c: "+354", i: "is" },
  { c: "+972", i: "il" }, { c: "+39", i: "it" }, { c: "+81", i: "jp" }, { c: "+967", i: "ye" },
  { c: "+27", i: "za" }, { c: "+82", i: "kr" }, { c: "+962", i: "jo" }, { c: "+855", i: "kh" },
  { c: "+237", i: "cm" }, { c: "+1", i: "us" }, { c: "+974", i: "qa" }, { c: "+7", i: "ru" },
  { c: "+254", i: "ke" }, { c: "+57", i: "co" }, { c: "+506", i: "cr" }, { c: "+53", i: "cu" },
  { c: "+965", i: "kw" }, { c: "+357", i: "cy" }, { c: "+996", i: "kg" }, { c: "+856", i: "la" },
  { c: "+961", i: "lb" }, { c: "+218", i: "ly" }, { c: "+423", i: "li" }, { c: "+370", i: "lt" },
  { c: "+371", i: "lv" }, { c: "+352", i: "lu" }, { c: "+36", i: "hu" }, { c: "+389", i: "mk" },
  { c: "+60", i: "my" }, { c: "+356", i: "mt" }, { c: "+212", i: "ma" }, { c: "+52", i: "mx" },
  { c: "+373", i: "md" }, { c: "+377", i: "mc" }, { c: "+976", i: "mn" }, { c: "+382", i: "me" },
  { c: "+95", i: "mm" }, { c: "+264", i: "na" }, { c: "+49", i: "de" }, { c: "+977", i: "np" },
  { c: "+234", i: "ng" }, { c: "+505", i: "ni" }, { c: "+31", i: "nl" }, { c: "+47", i: "no" },
  { c: "+64", i: "nz" }, { c: "+968", i: "om" }, { c: "+92", i: "pk" }, { c: "+507", i: "pa" },
  { c: "+595", i: "py" }, { c: "+51", i: "pe" }, { c: "+225", i: "ci" }, { c: "+48", i: "pl" },
  { c: "+351", i: "pt" }, { c: "+43", i: "at" }, { c: "+40", i: "ro" }, { c: "+250", i: "rw" },
  { c: "+966", i: "sa" }, { c: "+221", i: "sn" }, { c: "+381", i: "rs" }, { c: "+65", i: "sg" },
  { c: "+386", i: "si" }, { c: "+34", i: "es" }, { c: "+94", i: "lk" }, { c: "+41", i: "ch" },
  { c: "+46", i: "se" }, { c: "+963", i: "sy" }, { c: "+992", i: "tj" }, { c: "+886", i: "tw" },
  { c: "+255", i: "tz" }, { c: "+66", i: "th" }, { c: "+216", i: "tn" }, { c: "+90", i: "tr" },
  { c: "+993", i: "tm" }, { c: "+256", i: "ug" }, { c: "+380", i: "ua" }, { c: "+598", i: "uy" },
  { c: "+998", i: "uz" }, { c: "+44", i: "gb" }, { c: "+58", i: "ve" }, { c: "+84", i: "vn" },
  { c: "+260", i: "zm" }, { c: "+263", i: "zw" }, { c: "+971", i: "ae" },
];

function fmtDigits(d) {
  return d.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
}

// Rozparsuje uloženou hodnotu na předvolbu + číslice. Default +420.
function parseValue(value) {
  const s = String(value || "").trim();
  if (!s) return { prefix: "+420", digits: "" };
  const compact = s.replace(/[\s\-()]/g, "");
  if (compact.startsWith("+")) {
    // najdi nejdelší odpovídající předvolbu
    const sorted = [...DIAL_CODES].sort((a, b) => b.c.length - a.c.length);
    for (const dc of sorted) {
      if (compact.startsWith(dc.c)) return { prefix: dc.c, digits: compact.slice(dc.c.length).replace(/\D/g, "") };
    }
    // neznámá předvolba — vezmi +XX(X)
    const m = compact.match(/^(\+\d{1,3})(\d*)$/);
    if (m) return { prefix: m[1], digits: m[2] };
  }
  // bez předvolby → CZ
  return { prefix: "+420", digits: compact.replace(/\D/g, "") };
}

export default function PhoneInput({ value, onChange, disabled }) {
  const { prefix, digits } = useMemo(() => parseValue(value), [value]);
  const is9 = prefix === "+420" || prefix === "+421"; // CZ/SK = přesně 9 číslic
  const maxLen = is9 ? 9 : 12;

  const emit = (p, d) => {
    onChange(d ? `${p} ${fmtDigits(d)}` : "");
  };

  const handleDigits = (raw) => {
    const d = String(raw).replace(/\D/g, "").slice(0, maxLen);
    emit(prefix, d);
  };

  const invalid = digits.length > 0 && is9 && digits.length !== 9;

  return (
    <div>
      <div className="flex gap-2">
        <select
          value={prefix}
          onChange={(e) => emit(e.target.value, digits)}
          disabled={disabled}
          className="shrink-0 w-[7.5rem] text-base font-medium border border-slate-300 rounded-lg px-2 py-2 bg-white focus:outline-none focus:border-blue-500"
          title="Předvolba země"
        >
          {DIAL_CODES.map((dc) => (
            <option key={dc.c + dc.i} value={dc.c}>{flagOf(dc.i)} {dc.c}</option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="numeric"
          value={fmtDigits(digits)}
          onChange={(e) => handleDigits(e.target.value)}
          disabled={disabled}
          placeholder="777 777 777"
          className={`w-full text-base font-medium border rounded-lg px-3 py-2 focus:outline-none transition tabular-nums ${
            invalid ? "border-rose-300 focus:border-rose-500 bg-rose-50" : "border-slate-300 focus:border-blue-500"
          }`}
        />
      </div>
      {invalid && <span className="text-xs text-rose-600 mt-1 inline-block">Zadej přesně 9 číslic (např. 777 777 777)</span>}
    </div>
  );
}
